export const MOBILE_BENTO_TRANSITION_OPTIONS = Object.freeze({
  duration: 600,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
  fill: 'both',
});

export function canAnimateMobileBentoTransition({
  mobile = false,
  reducedMotion = false,
  count = 0,
  currentIndex = 0,
  targetIndex = 0,
} = {}) {
  return mobile && !reducedMotion && count > 1 && currentIndex !== targetIndex;
}

export function mobileBentoTransitionFrames(direction = 1, releaseOffset = 0) {
  const sign = Number(direction) < 0 ? -1 : 1;
  const startOffset = Number.isFinite(Number(releaseOffset)) ? Number(releaseOffset) : 0;
  return {
    incoming: [
      { opacity: 0.9, transform: `translate3d(${sign * 100}%, 0, 0) scale(1.015)` },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
    ],
    outgoing: [
      { opacity: 1, transform: `translate3d(${startOffset}px, 0, 0) scale(1)` },
      { opacity: 0.9, transform: `translate3d(${-sign * 100}%, 0, 0) scale(0.985)` },
    ],
    media: [
      { transform: `translate3d(${-sign * 8}%, 0, 0) scale(1.06)` },
      { transform: 'translate3d(0, 0, 0) scale(1)' },
    ],
  };
}

export class BentoAutoplayController {
  constructor({
    intervalMs = 6000,
    onTick,
    setIntervalFn = globalThis.setInterval.bind(globalThis),
    clearIntervalFn = globalThis.clearInterval.bind(globalThis),
  }) {
    this.intervalMs = Math.max(4000, Number(intervalMs) || 6000);
    this.onTick = onTick;
    this.setIntervalFn = setIntervalFn;
    this.clearIntervalFn = clearIntervalFn;
    this.timer = null;
    this.state = {
      enabled: false,
      count: 0,
      hidden: false,
      reducedMotion: false,
      hovered: false,
      focused: false,
      touching: false,
    };
  }

  update(patch) {
    Object.assign(this.state, patch);
    this.sync();
  }

  canRun() {
    const state = this.state;
    return state.enabled && state.count > 1 && !state.hidden && !state.reducedMotion
      && !state.hovered && !state.focused && !state.touching;
  }

  sync() {
    if (!this.canRun()) {
      if (this.timer !== null) this.clearIntervalFn(this.timer);
      this.timer = null;
      return;
    }
    if (this.timer !== null) return;
    this.timer = this.setIntervalFn(this.onTick, this.intervalMs);
  }

  destroy() {
    if (this.timer !== null) this.clearIntervalFn(this.timer);
    this.timer = null;
  }
}

export class LSFeaturedBento extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.hero = this.querySelector('.ls-featured-bento__hero');
    this.slides = [...this.querySelectorAll('[data-ls-bento-slide]')];
    this.heroLinks = [...this.querySelectorAll('[data-ls-bento-hero-link]')];
    this.heroContents = [...this.querySelectorAll('[data-ls-bento-content]')];
    this.dots = [...this.querySelectorAll('[data-ls-bento-dot]')];
    this.previousButton = this.querySelector('[data-ls-bento-previous]');
    this.nextButton = this.querySelector('[data-ls-bento-next]');
    this.liveRegion = this.querySelector('[data-ls-bento-live]');
    this.currentIndex = Number(this.dataset.currentIndex) || 0;
    this.touchStart = null;
    this.dragOffset = 0;
    this.layoutQuery = globalThis.matchMedia?.('(max-width: 989px)');
    this.motionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    this.transition = null;
    this.transitionSequence = 0;
    this.transitionTimer = null;
    this.suppressClickTimer = null;
    this.suppressNextHeroClick = false;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.controller = new BentoAutoplayController({
      intervalMs: this.dataset.autoplayInterval,
      onTick: () => this.next({ announce: false }),
    });

    this.addEventListener('mouseenter', () => this.updateController({ hovered: true }), { signal });
    this.addEventListener('mouseleave', () => this.updateController({ hovered: false }), { signal });
    this.addEventListener('focusin', () => this.updateController({ focused: true }), { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => this.updateController({ focused: this.matches(':focus-within') }));
    }, { signal });
    this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
    this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.addEventListener('pointercancel', (event) => this.onPointerCancel(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.hero?.addEventListener('click', (event) => this.onHeroClick(event), { capture: true, signal });
    document.addEventListener('visibilitychange', () => {
      this.updateController({ hidden: document.hidden });
    }, { signal });
    this.motionQuery?.addEventListener('change', (event) => {
      this.updateController({ reducedMotion: event.matches });
      if (event.matches) this.settleMobileMotion();
    }, { signal });
    this.layoutQuery?.addEventListener('change', (event) => {
      if (!event.matches) this.settleMobileMotion();
    }, { signal });
    this.previousButton?.addEventListener('click', () => this.previous(), { signal });
    this.nextButton?.addEventListener('click', () => this.next(), { signal });
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => {
      const direction = index === this.currentIndex ? 0 : (index > this.currentIndex ? 1 : -1);
      this.goTo(index, { direction });
    }, { signal }));

    this.setAttribute('data-ready', '');
    this.goTo(this.currentIndex, { animate: false, announce: false });
    this.updateController({
      enabled: this.dataset.autoplay !== 'false' && this.dataset.designMode !== 'true',
      count: this.slides.length,
      hidden: document.hidden,
      reducedMotion: this.motionQuery?.matches || false,
    });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.controller?.destroy();
    this.cancelTransition({ settle: false });
    if (this.suppressClickTimer !== null) globalThis.clearTimeout?.(this.suppressClickTimer);
    this.controller = null;
  }

  goTo(index, {
    animate = true,
    announce = true,
    direction = 0,
    releaseOffset = 0,
  } = {}) {
    this.cancelTransition();
    const count = this.slides.length;
    if (!count) return;
    const targetIndex = ((Number(index) % count) + count) % count;
    const resolvedDirection = Number(direction) || (targetIndex > this.currentIndex ? 1 : -1);
    const canAnimate = animate
      && canAnimateMobileBentoTransition({
        mobile: this.layoutQuery?.matches || false,
        reducedMotion: this.motionQuery?.matches || false,
        count,
        currentIndex: this.currentIndex,
        targetIndex,
      })
      && typeof this.slides[this.currentIndex]?.animate === 'function'
      && typeof this.slides[targetIndex]?.animate === 'function';

    if (canAnimate) {
      this.startTransition(targetIndex, resolvedDirection, { announce, releaseOffset });
      return;
    }

    this.applyIndex(targetIndex, { announce });
  }

  applyIndex(index, { announce = true } = {}) {
    const count = this.slides.length;
    if (!count) return;
    this.currentIndex = ((Number(index) % count) + count) % count;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.currentIndex;
      slide.classList.remove('is-entering', 'is-leaving');
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.updateUi({ announce });
  }

  updateUi({ announce = true } = {}) {
    const count = this.slides.length;
    [this.heroLinks || [], this.heroContents || []].forEach((items) => {
      items.forEach((item, itemIndex) => {
        const active = itemIndex === this.currentIndex;
        item.classList.toggle('is-active', active);
        item.hidden = !active;
        item.inert = !active;
        item.setAttribute('aria-hidden', String(!active));
      });
    });
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === this.currentIndex)));
    this.dataset.currentIndex = String(this.currentIndex);
    if (announce && this.liveRegion) this.liveRegion.textContent = `Featured image ${this.currentIndex + 1} of ${count}`;
  }

  startTransition(targetIndex, direction, { announce = true, releaseOffset = 0 } = {}) {
    const outgoing = this.slides[this.currentIndex];
    const incoming = this.slides[targetIndex];
    if (!outgoing || !incoming) {
      this.applyIndex(targetIndex, { announce });
      return;
    }

    const frames = mobileBentoTransitionFrames(direction, releaseOffset);
    this.currentIndex = targetIndex;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === targetIndex;
      const visible = active || slide === outgoing;
      slide.classList.toggle('is-active', active);
      slide.hidden = !visible;
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    outgoing.classList.add('is-leaving');
    incoming.classList.add('is-entering');
    this.updateUi({ announce });

    const animations = [
      outgoing.animate(frames.outgoing, MOBILE_BENTO_TRANSITION_OPTIONS),
      incoming.animate(frames.incoming, MOBILE_BENTO_TRANSITION_OPTIONS),
    ];
    const incomingMedia = incoming.querySelector('img, svg');
    if (typeof incomingMedia?.animate === 'function') {
      animations.push(incomingMedia.animate(frames.media, MOBILE_BENTO_TRANSITION_OPTIONS));
    }

    const token = ++this.transitionSequence;
    this.transition = { animations, incoming, outgoing, token };
    this.transitionTimer = globalThis.setTimeout?.(
      () => this.finishTransition(token),
      MOBILE_BENTO_TRANSITION_OPTIONS.duration + 80,
    ) ?? null;
    Promise.allSettled(animations.map((animation) => animation.finished).filter(Boolean))
      .then(() => this.finishTransition(token));
  }

  finishTransition(token) {
    if (!this.transition || this.transition.token !== token) return;
    const transition = this.transition;
    this.transition = null;
    if (this.transitionTimer !== null) globalThis.clearTimeout?.(this.transitionTimer);
    this.transitionTimer = null;
    transition.animations.forEach((animation) => animation.cancel?.());
    transition.outgoing.classList.remove('is-leaving');
    transition.incoming.classList.remove('is-entering');
    this.applyIndex(this.currentIndex, { announce: false });
    this.resetDrag();
  }

  cancelTransition({ settle = true } = {}) {
    if (!this.transition) return;
    const transition = this.transition;
    this.transition = null;
    this.transitionSequence += 1;
    if (this.transitionTimer !== null) globalThis.clearTimeout?.(this.transitionTimer);
    this.transitionTimer = null;
    transition.animations.forEach((animation) => animation.cancel?.());
    transition.outgoing.classList.remove('is-leaving');
    transition.incoming.classList.remove('is-entering');
    if (settle) this.applyIndex(this.currentIndex, { announce: false });
  }

  settleMobileMotion() {
    this.cancelTransition();
    this.resetDrag();
  }

  next(options = {}) {
    this.goTo(this.currentIndex + 1, { ...options, direction: 1 });
  }

  previous(options = {}) {
    this.goTo(this.currentIndex - 1, { ...options, direction: -1 });
  }

  updateController(patch) {
    this.controller?.update(patch);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'touch' || !event.target?.closest?.('.ls-featured-bento__hero')) return;
    this.touchStart = { x: event.clientX, y: event.clientY };
    this.dragOffset = 0;
    this.updateController({ touching: true });
  }

  onPointerMove(event) {
    if (event.pointerType !== 'touch' || !this.touchStart || !this.layoutQuery?.matches || this.motionQuery?.matches) return;
    const distanceX = event.clientX - this.touchStart.x;
    const distanceY = event.clientY - this.touchStart.y;
    if (Math.abs(distanceX) < 10 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
    event.preventDefault?.();
    const limit = Math.min((this.hero?.clientWidth || 0) * 0.14, 48) || 48;
    this.dragOffset = Math.max(-limit, Math.min(limit, distanceX * 0.22));
    this.hero?.style?.setProperty('--ls-bento-drag-x', `${this.dragOffset}px`);
    this.hero?.setAttribute('data-ls-bento-dragging', '');
  }

  onPointerUp(event) {
    if (event.pointerType !== 'touch' || !this.touchStart) return;
    const distanceX = event.clientX - this.touchStart.x;
    const distanceY = event.clientY - this.touchStart.y;
    const releaseOffset = this.dragOffset;
    this.touchStart = null;
    this.updateController({ touching: false });
    this.hero?.removeAttribute('data-ls-bento-dragging');
    if (Math.abs(distanceX) < 40 || Math.abs(distanceX) <= Math.abs(distanceY)) {
      this.resetDrag();
      return;
    }
    this.suppressNextHeroClick = true;
    if (this.suppressClickTimer !== null) globalThis.clearTimeout?.(this.suppressClickTimer);
    this.suppressClickTimer = globalThis.setTimeout?.(() => {
      this.suppressNextHeroClick = false;
      this.suppressClickTimer = null;
    }, 700) ?? null;
    if (distanceX < 0) this.next({ releaseOffset });
    else this.previous({ releaseOffset });
    this.resetDrag();
  }

  onPointerCancel(event) {
    if (event.pointerType !== 'touch') return;
    this.touchStart = null;
    this.updateController({ touching: false });
    this.resetDrag();
  }

  resetDrag() {
    this.dragOffset = 0;
    this.hero?.removeAttribute('data-ls-bento-dragging');
    this.hero?.style?.removeProperty('--ls-bento-drag-x');
  }

  onHeroClick(event) {
    if (!this.suppressNextHeroClick) return;
    this.suppressNextHeroClick = false;
    if (this.suppressClickTimer !== null) globalThis.clearTimeout?.(this.suppressClickTimer);
    this.suppressClickTimer = null;
    event.preventDefault?.();
    event.stopPropagation?.();
    event.stopImmediatePropagation?.();
  }

  onKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }
}

if (!customElements.get('ls-featured-bento')) customElements.define('ls-featured-bento', LSFeaturedBento);
